import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, inr } from "@/lib/format";

const COLS = [
  { key: "line_no", label: "#" }, { key: "description", label: "Item", wrap: true },
  { key: "ordered_qty", label: "Ordered", align: "right" }, { key: "dispatched_qty", label: "Dispatched", align: "right" },
  { key: "invoiced_qty", label: "Invoiced", align: "right" }, { key: "balance_qty", label: "Balance qty (to nullify)", align: "right" },
  { key: "balance_value", label: "Balance value", align: "right", render: (r) => inr(r.balance_value) },
];
const sum = (oi, k) => oi.lines.reduce((s, x) => s + (x[k] || 0), 0);

export const OverInvoiced = ({ oi, testid = "sc-over-invoiced" }) => (
  <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 space-y-1" data-testid={testid}>
    <p className="font-semibold">WARNING: This LM is over-invoiced. Invoiced {sum(oi, "invoiced_qty")} but dispatched only {sum(oi, "dispatched_qty")} on {oi.lines.length} line(s) (excess value {inr(oi.total_excess_value)}). Finance must raise a credit note / correct the invoices before this short close can be approved. Finance will be notified when you submit.</p>
    {oi.lines.map((x) => <p key={x.line}>Line {x.line}: invoiced {x.invoiced_qty}, dispatched {x.dispatched_qty}, excess {x.excess_qty} ({inr(x.excess_value)}) · invoices {x.invoice_nos.join(", ")}</p>)}
  </div>
);

// operations: request a short close of a partly dispatched LM
export function ShortCloseDialog({ order, onClose, onDone }) {
  const { data: pv } = useFetch(`/orders/${order.id}/short-close-preview`);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!pv) return null;
  const oi = pv.over_invoiced;
  const go = async () => {
    if (reason.trim().length < 3) return toast.error("Enter the reason for short close (at least 3 characters)");
    setBusy(true);
    try {
      const r = await api.post(`/orders/${order.id}/short-close-request`, { reason, ack_over_invoiced: ack });
      toast.success("Short close sent to Admin for approval");
      if (r.data.finance_mail_warning) toast.warning(r.data.finance_mail_warning);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="short-close-dialog">
        <DialogHeader><DialogTitle>Short Close · {order.order_no}</DialogTitle></DialogHeader>
        {oi && <OverInvoiced oi={oi} />}
        <p className="text-xs text-slate-500">The remaining balance below will not be dispatched. Once an Admin approves, balance qty and value become zero; ordered, dispatched and invoiced figures stay as history.</p>
        <DataTable testid="short-close-lines" columns={COLS} rows={pv.lines} rowId={(r) => r.line_item_id} />
        <p className="text-sm" data-testid="short-close-total">Total balance: <b>{pv.total_balance_qty}</b> qty · <b>{inr(pv.total_balance_value)}</b></p>
        <p className="text-xs text-slate-600" data-testid="short-close-plans">Open planned dispatches affected: {pv.affected_plans.join(", ") || "none"}</p>
        <div><Label>Reason for short close *</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="short-close-reason" /></div>
        {oi && <label className="flex items-center gap-2 text-xs text-red-800"><Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="short-close-ack" />I understand finance must correct the invoices first</label>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="short-close-cancel">Cancel</Button>
          <Button disabled={busy || (oi && !ack)} onClick={go} data-testid="short-close-submit">Send for Admin approval</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminDecision({ o, reload }) {
  const { data: pv } = useFetch(`/orders/${o.id}/short-close-preview`);
  const [busy, setBusy] = useState(false);
  const r = o.short_close_request;
  const oi = pv?.over_invoiced;
  const decide = async (ok) => {
    const comment = ok ? "" : window.prompt("Comment for rejecting this short close?");
    if (!ok && !comment) return;
    setBusy(true);
    try { await api.post(`/orders/${o.id}/short-close-request/decision`, { decision: ok ? "approved" : "rejected", comment }); toast.success(ok ? "LM short-closed" : "Short close rejected"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!pv) return null;
  return (
    <div className="mt-2 space-y-2">
      {oi && <p className="rounded border-2 border-red-400 bg-red-100 px-3 py-2 text-xs font-semibold text-red-900" data-testid="sc-admin-do-not-approve">DO NOT APPROVE YET - this LM is over-invoiced by {inr(oi.total_excess_value)} ({oi.lines.map((x) => `line ${x.line}`).join(", ")}). Finance must issue a credit note / correct the invoices first. Approving now will leave invoices for quantity that was never dispatched.</p>}
      {!oi && r.over_invoiced && <p className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="sc-admin-resolved">Over-invoicing resolved by finance: {r.finance_resolved?.note || "invoices corrected"}</p>}
      <div className="flex gap-2">
        <Button size="sm" disabled={busy || !!oi} title={oi ? "LM is still over-invoiced" : ""} onClick={() => decide(true)} data-testid="sc-approve-btn">Approve short close</Button>
        <Button size="sm" variant="outline" className="text-red-700" disabled={busy} onClick={() => decide(false)} data-testid="sc-reject-btn">Reject</Button>
      </div>
    </div>
  );
}

// pending request banner on the LM, with withdraw / finance resolved / Admin decision
export function ShortCloseBanner({ o, reload }) {
  const { can, me } = useMe();
  const r = o.short_close_request;
  if (r?.status !== "pending") return null;
  const isAdmin = me.roles.includes("admin");
  const act = async (url, body, ok) => { try { const x = await api.post(url, body); toast.success(ok); if (x.data.still_over_invoiced) toast.warning(`Still over-invoiced by ${inr(x.data.still_over_invoiced.total_excess_value)}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const resolve = () => { const n = window.prompt("Finance resolution note (e.g. credit note number)"); if (n) act(`/orders/${o.id}/short-close-request/finance-resolved`, { note: n }, "Finance resolution recorded"); };
  return (
    <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="sc-pending-banner">
      <p>Short close requested by <b>{r.requested_by?.name}</b> - waiting for Admin approval: {r.reason} (balance {r.total_balance_qty} qty · {inr(r.total_balance_value)})</p>
      {r.over_invoiced && <p className="mt-1 font-semibold text-red-700" data-testid="sc-banner-over-invoiced">Over-invoiced - finance to correct invoices before approval</p>}
      {r.finance_resolved && <p className="mt-1 text-emerald-800" data-testid="sc-banner-finance-note">Finance: {r.finance_resolved.note} ({r.finance_resolved.by?.name}, {fmtDateTime(r.finance_resolved.at)})</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {(r.requested_by?.user_id === me.user_id || isAdmin) && <Button size="sm" variant="outline" className="h-7" onClick={() => act(`/orders/${o.id}/short-close-request/withdraw`, {}, "Request withdrawn")} data-testid="sc-withdraw-btn">Withdraw request</Button>}
        {r.over_invoiced && (isAdmin || can("dispatch.edit")) && <Button size="sm" variant="outline" className="h-7" onClick={resolve} data-testid="sc-finance-resolved-btn">Finance resolved</Button>}
      </div>
      {isAdmin ? <AdminDecision key={r.finance_resolved?.at || r.id} o={o} reload={reload} /> : <p className="mt-1 text-[11px] text-slate-600" data-testid="sc-admin-only-note">Only an Admin can approve or reject this short close.</p>}
    </div>
  );
}

export const ShortClosedInfo = ({ o }) => {
  const s = o.short_closed;
  if (!s || o.is_projection) return null;
  return (
    <div className="mb-3 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="sc-closed-info">
      <b>Short-closed</b> · {s.total_qty} qty · {inr(s.total_value)} nullified · reason: {s.reason} · requested by {s.requested_by?.name} · approved by {s.approved_by?.name} on {fmtDateTime(s.approved_at)}
      {s.finance_resolved && <span> · finance: {s.finance_resolved.note}</span>}
    </div>
  );
};
